import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ControlsOf,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Address } from '@digital-platform/shared/domain';
import {
  ChangeRequestDetailsModel,
  WasteStreamOptions,
} from '@digital-platform/waste-profiles/domain';

type Details = Pick<
  ChangeRequestDetailsModel,
  | 'generatorName'
  | 'changeReason'
  | 'volumeIncrease'
  | 'unitOfMeasure'
  | 'expiryDate'
  | 'otherChanges'
  | 'generatorAddress'
>;

type DetailsFormComponents = ControlsOf<
  Omit<Details, 'expiryDate' | 'generatorAddress'>
> & {
  expiryDate: FormControl<Date>;
  generatorAddress: FormControl<Address>;
};

@Component({
  selector: 'digital-platform-waste-profile-change-request-form',
  templateUrl: './waste-profile-change-request-form.component.html',
  styleUrls: ['./waste-profile-change-request-form.component.scss'],
})
export class WasteProfileChangeRequestFormComponent implements OnInit {
  public form: FormGroup<DetailsFormComponents>;
  @Input() public value: Details;
  @Output() public readonly valueChanged = new EventEmitter<Details>();

  private destroyed$ = new Subject<void>();
  public today = new Date();
  streamOptions: WasteStreamOptions = {
    options: [
      'Cubic Yards',
      'Tons',
      'Gallons',
      'Drums',
      'Pounds',
      'Pallets',
      'Totes',
    ],
  };

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    let generatorStream: Details = ChangeRequestDetailsModel.DEFAULT_CONFIG;
    if (this.value) {
      generatorStream = this.value;
    }
    if (this.form) {
      this.form.patchValue(generatorStream, {
        emitEvent: false,
      });
    } else {
      this.buildForm(generatorStream);
    }
    this.onVolumeIncrease();
  }

  private buildForm(generatorForm: Details) {
    this.form = this.fb.group({
      changeReason: [generatorForm.changeReason, Validators.required],
      volumeIncrease: [
        generatorForm.volumeIncrease === 0
          ? null
          : generatorForm.volumeIncrease,
        [Validators.max(999999), Validators.pattern(/^(0|-?[1-9][0-9]*)$/)],
      ],
      unitOfMeasure: [generatorForm.unitOfMeasure],
      expiryDate: [generatorForm.expiryDate],
      generatorName: [generatorForm.generatorName],
      otherChanges: [generatorForm.otherChanges],
      generatorAddress: this.fb.control<Address>({
        addressLine1: generatorForm.generatorAddress?.addressLine1,
        addressLine2: generatorForm.generatorAddress?.addressLine2,
        addressLine3: generatorForm.generatorAddress?.addressLine3,
        city: generatorForm.generatorAddress?.city,
        county: generatorForm.generatorAddress?.county,
        stateCode: generatorForm.generatorAddress?.stateCode,
        postalCode: generatorForm.generatorAddress?.postalCode,
        postalCodeSuffix: generatorForm.generatorAddress?.postalCodeSuffix,
        country: generatorForm.generatorAddress?.country || 'US',
      }),
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.valueChanged.emit({
          changeReason: value.changeReason,
          generatorName: value.generatorName,
          volumeIncrease: _.toNumber(value.volumeIncrease),
          unitOfMeasure: value.unitOfMeasure,
          expiryDate: value.expiryDate,
          otherChanges: value.otherChanges,
          generatorAddress: value?.generatorAddress,
        });
      });
  }

  errorHandling = (control: string, error: string) =>
    this.form.controls[control].hasError(error);

  onVolumeIncrease = () => {
    const unitOfMeasureControl = this.form.get('unitOfMeasure');

    this.form.get('volumeIncrease').valueChanges.subscribe((val) => {
      if (val) {
        unitOfMeasureControl.setValidators([Validators.required]);
      } else {
        unitOfMeasureControl.clearValidators();
      }
      unitOfMeasureControl.updateValueAndValidity();
    });
  };
}
